import React, { useEffect, useRef, useState } from "react";
import { Snackbar } from "@mui/material";
import { useSelector } from "react-redux";
import { useOrganizationId } from "../../../features/auth/useOrganizationId";
import { selectUserDetails } from "../../../features/auth/authSlice";
import {
	useCreditVendorsMutation,
	getCreditVendorDetailsQueryArgs,
	useGetCreditVendorDetailsQuery,
	useDeleteCreditVendorDetailsMutation,
	filterVisibleCreditVendors,
} from "../../../features/credit-reissue/creditVendorsApiSlice";
import LoaderModal from "../../../components/modal/loader-modal";
import CustomAlert from "../../../components/custom-alert";
import CreditProviderCard from "./components/CreditProviderCard";
import AddProviderButton from "./components/AddProviderButton";
import AddProviderForm from "./components/AddProviderForm";
import useErrorMessages from "../../../hooks/useErrorMessages";
import { logErrorToCloudWatch } from "../../../utils/rum";
import styles from "./index.module.scss";

const normalizeValue = (value) => String(value ?? "").trim().toLowerCase();

const CreditReissueConfiguration = ({
	isCompanyAdmin = false,
	isLoanOfficerOrProcessor = false,
}) => {
	const organizationId = useOrganizationId();
	const userDetails = useSelector(selectUserDetails);
	const creditVendorDetailsQuery = getCreditVendorDetailsQueryArgs(
		organizationId,
		userDetails
	);
	const [
		creditVendors,
		{
			data: vendors = [],
			isLoading,
			isError,
			error: creditVendorsError,
			isSuccess,
		},
	] = useCreditVendorsMutation();
	const {
		currentData: creditProviders,
		isLoading: isLoadingProviders,
		isError: isProvidersError,
		refetch: refetchProviders,
	} = useGetCreditVendorDetailsQuery(creditVendorDetailsQuery, {
		skip: !creditVendorDetailsQuery,
	});
	const [providers, setProviders] = useState([]);
	const [editingProviderId, setEditingProviderId] = useState(null);
	const [deletingProviderId, setDeletingProviderId] = useState(null);
	const [successToast, setSuccessToast] = useState(null);
	const nextProviderId = useRef(1);
	const hasLoggedCreditVendorsError = useRef(false);
	const { getErrorMessage } = useErrorMessages();
	const creditVendorsLoadError = getErrorMessage(
		"CREDIT_VENDOR_LIST_FAILED_TO_LOAD",
		creditVendorsError
	);
	const providerOptions = vendors
		.map((vendor) => ({
			...vendor,
			label: vendor.partner_name,
			value: vendor.partner_name,
			group: vendor.group || "Other Vendors",
		}))
		.sort(
			(firstVendor, secondVendor) =>
				firstVendor.label.localeCompare(secondVendor.label, undefined, {
					sensitivity: "base",
				})
		);
	const getVendorGroup = (creditVendor) =>
		vendors.find(
			(vendor) =>
				normalizeValue(vendor.partner_name) === normalizeValue(creditVendor)
		)?.group;
	const hasSubscriberIdAtCompanyOrUserLevel = (creditVendor) =>
		(creditProviders ?? []).some(
			(provider) =>
				normalizeValue(provider.credit_vendor ?? provider.creditVendor) ===
					normalizeValue(creditVendor) &&
				Boolean(
					String(provider.subscriberId ?? provider.subscriber_id ?? "").trim()
				)
		);

	useEffect(() => {
		creditVendors();
	}, [creditVendors]);

	useEffect(() => {
		if (!isError) {
			hasLoggedCreditVendorsError.current = false;
			return;
		}

		if (hasLoggedCreditVendorsError.current) return;

		hasLoggedCreditVendorsError.current = true;
		logErrorToCloudWatch({
			error_code: creditVendorsLoadError.code,
			title: creditVendorsLoadError.title,
			message: creditVendorsLoadError.message,
			error_response: creditVendorsError,
		});
	}, [
		isError,
		creditVendorsError,
		creditVendorsLoadError.code,
		creditVendorsLoadError.title,
		creditVendorsLoadError.message,
	]);

	useEffect(() => {
		setEditingProviderId(null);
		setProviders(
			filterVisibleCreditVendors(creditProviders, {
				accessLevel: userDetails?.access_level,
				userEmail: userDetails?.email,
			}).map((provider) => ({
				id: provider.id,
				name: provider.creditVendor,
				userName: provider.userName,
				user_email: provider.user_email,
				subscriberId: provider.subscriberId ?? provider.subscriber_id,
				encryptedPassword: provider.encrypted_password,
				setByAdmin: provider.set_by_admin,
				group: getVendorGroup(provider.creditVendor),
				hasSubscriberIdAtCompanyOrUserLevel:
					hasSubscriberIdAtCompanyOrUserLevel(provider.creditVendor),
				username: provider.userName,
				password: "••••••••",
				configured: true,
			}))
		);
	}, [
		creditProviders,
		vendors,
		userDetails?.access_level,
		userDetails?.email,
	]);

	const configuredCount = providers.filter(
		(provider) => provider.configured
	).length;

	const addProviderForm = () => {
		const id = `draft-${nextProviderId.current++}`;
		setProviders((current) => [...current, { id, isDraft: true }]);
	};

	const saveProvider = (currentId, savedProviderId, name, details) => {
		const action = providers.find((provider) => provider.id === currentId)?.isDraft
			? "added"
			: "updated";
		setSuccessToast({
			message: `${name} has been ${action} to your Credit Reissue Configuration..`,
			key: Date.now(),
		});
		setEditingProviderId(null);
		// Keep only masked credentials in the provider cards.
		setProviders((current) =>
			current.map((provider) =>
				provider.id === currentId
					? {
							id: savedProviderId,
							name,
							userName: details.userName,
							user_email: details.user_email,
							subscriberId: details.subscriberId,
							setByAdmin: details.setByAdmin,
							group: getVendorGroup(name),
							hasSubscriberIdAtCompanyOrUserLevel:
								hasSubscriberIdAtCompanyOrUserLevel(name) ||
								Boolean(String(details.subscriberId ?? "").trim()),
							username: provider.userName,
							password: "••••••••",
							configured: true,
						}
					: provider
			)
		);
		refetchProviders();
	};

	const cancelProviderForm = (id) => {
		setProviders((current) => current.filter((provider) => provider.id !== id));
	};

	const [deleteCreditVendorDetails] = useDeleteCreditVendorDetailsMutation();

	const handleDelete = async (id) => {
		setDeletingProviderId(id);

		try {
			await deleteCreditVendorDetails({ id }).unwrap();
			refetchProviders();

			setSuccessToast({
				message: `Provider deleted successfully.`,
				key: Date.now(),
			});
		} catch (error) {
			console.error("Unable to delete provider", error);
		} finally {
			setDeletingProviderId(null);
		}
	};

	return (
		<section
			className={styles.configuration}
			aria-labelledby="credit-reissue-title"
		>
			<header className={styles.header}>
				<div>
					<h1 id="credit-reissue-title" className={styles.title}>
						Credit Reissue Configuration
					</h1>
					<p className={styles.description}>
						Manage your saved credit providers. Each provider's credentials are
						stored separately and will pre-populate at the loan level.
					</p>
				</div>
				<span className={styles.configuredBadge}>
					<span aria-hidden="true">✓ </span>
					{configuredCount} {configuredCount === 1 ? "provider" : "providers"}{" "}
					configured
				</span>
			</header>
			<div className={styles.providerList}>
				{isProvidersError && (
					<CustomAlert
						severity="error"
						variant="outlined"
						details="Unable to load saved credit providers. Please try again."
						showClearButton
						clearButtonText="Retry"
						onClearMessage={() => refetchProviders()}
					/>
				)}
				{isError && (
					<CustomAlert
						severity="error"
						variant="outlined"
						subject={creditVendorsLoadError.title}
						details={creditVendorsLoadError.message}
						showClearButton
						clearButtonText="Retry"
						onClearMessage={() => creditVendors()}
					/>
				)}
				{!isLoadingProviders && !isProvidersError && providers.length === 0 && (
					<div className={styles.emptyState}>
						No credit vendors configured yet. Add a vendor to get started.
					</div>
				)}
				{providers.map((provider) =>
					provider.isDraft || editingProviderId === provider.id ? (
						<AddProviderForm
							key={provider.id}
							provider={provider.isDraft ? undefined : provider}
							providerOptions={providerOptions}
							adminConfiguredProviders={creditProviders ?? []}
							organizationId={organizationId}
							isCompanyAdmin={isCompanyAdmin}
							isSubscriberIdReadOnly={
								isLoanOfficerOrProcessor && !isCompanyAdmin
							}
							onSave={(name, details) =>
							saveProvider(
								provider.id,
								details.id ?? provider.id,
								name,
								details
							)
							}
							onCancel={() =>
								provider.isDraft
									? cancelProviderForm(provider.id)
									: setEditingProviderId(null)
							}
						/>
					) : (
						<CreditProviderCard
							key={provider.id}
							provider={provider}
							onEdit={() => setEditingProviderId(provider.id)}
							onDelete={() => handleDelete(provider.id)}
							isCompanyAdmin={isCompanyAdmin}
							currentUserEmail={userDetails?.email}
						/>
					)
				)}
			</div>
			<AddProviderButton
				onClick={addProviderForm}
				disabled={
					!organizationId ||
					isLoadingProviders ||
					!isSuccess ||
					providerOptions.length === 0
				}
			/>
			<p className={styles.information}>
				<span aria-hidden="true">ℹ </span>
				Each provider's credentials are stored separately. Adding or editing one
				provider will not affect any other saved provider. Credentials are
				pre-populated at the credit step on a loan — you can always override
				them at the loan level.
			</p>
			<LoaderModal
				open={isLoading || isLoadingProviders}
				text="Loading credit vendors"
			/>
			<Snackbar
				key={successToast?.key}
				open={Boolean(successToast)}
				autoHideDuration={4000}
				anchorOrigin={{ vertical: "top", horizontal: "right" }}
				onClose={(_, reason) => {
					if (reason !== "clickaway") setSuccessToast(null);
				}}
			>
				<div className={styles.successToast} role="status">
					<span aria-hidden="true">✓</span> {successToast?.message}
				</div>
			</Snackbar>
		</section>
	);
};

export default CreditReissueConfiguration;
